import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import Button from '../shared/TailwindComponents/Button';
import { CheckIcon } from '@heroicons/react/24/outline';
import {
PiBuildingOfficeLight,
  PiFadersLight,
PiUserCircleCheckLight,
} from 'react-icons/pi';

const WelcomeStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const logoUrl = '/logosvg.svg';
  const onboardingUser = useSelector((state: RootState) => state.onboarding.user);

  const handleStartOnboarding = () => {
    // Skip organization step for invited users who already have an org assigned
    const nextStep = onboardingUser?.organization_id ? 'user' : 'organization';
    dispatch({
      type: 'ONBOARDING_SET_STEP',
      payload: nextStep,
    });
  };

  return (
    <div className="w-full max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-light dark:shadow-dark p-8">
      <div className="flex flex-col">
        {/* Logo */}
        <div className="flex justify-center mb-4">
          <div className="w-12 h-12 flex items-center justify-center">
            <img src={logoUrl} alt={t('PONS Logo')} className="w-full h-full object-contain" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">
          {t('Welcome to PONS')}
        </h1>

        {/* Description */}
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 text-center">
          {t("Let's set up your account in just a few steps. This will take about 5 minutes.")}
        </p>

        {/* Features List */}
        <div className="bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-primary-900/10 dark:to-secondary-900/10 rounded-lg p-4 mb-4">
          <div className="space-y-4 text-left">
            <div className="flex items-start gap-2">
              <div className="flex-shrink-0 mt-0.5">
                <PiBuildingOfficeLight className="w-6 h-6 text-black dark:text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('Set up your organization')}</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400">{t('Confirm or join an organization')}</p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <div className="flex-shrink-0 mt-0.5">
                <PiUserCircleCheckLight className="w-6 h-6 text-black dark:text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('Complete your profile')}</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400">{t('Add your name and confirm role')}</p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <div className="flex-shrink-0 mt-0.5">
                <PiFadersLight className="w-6 h-6 text-black dark:text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('Set your preferences')}</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400">{t('Configure language and settings')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Button */}
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="small"
            onClick={handleStartOnboarding}
          >
            {t("Let's Get Started")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeStep;
